import { useEffect, useState } from "react";
import { useLocation, useParams } from "react-router-dom";
import { Loader2, Container } from "lucide-react";
import { toast } from "react-toastify";

import { useAppDispatch, useAppSelector } from "../../hooks/useStore";
import {
  fetchContainerOrder,
  generateContainerLines,
  clearCurrentContainerOrder,
} from "../../store/slices/containerOrdersSlice";
import { ContainerOrderForm } from "../../components/ContainerOrderForm/ContainerOrderForm";
import { GenerateLinesModal } from "../../components/ContainerOrderForm/GenerateLinesModal";
import { OrderLinesPanel } from "../../components/ContainerOrderForm/OrderLinesPanel";
import { SectionCard } from "../../components/UI/SectionCard";
import type {
  ContainerOrderCopyData,
  ContainerOrderCopySource,
  GenerateLinesPayload,
} from "../../types/containerOrders";
import { useTranslation } from "react-i18next";

/**
 * Container order detail.
 *
 * The order form itself never lists containers — that block lives here, below
 * the form, so creating and editing an order stays focused on the shared
 * defaults while the batch is reviewed separately. The batch renders through the
 * same OrderLinesPanel the orders list expands to.
 */
export default function ContainerOrderPage() {
  const { orderId } = useParams<{ orderId: string }>();
  const location = useLocation();
  const { t } = useTranslation(["containerOrders", "shipments"]);
  const dispatch = useAppDispatch();

  const { current, loading, generating } = useAppSelector(
    (state) => state.containerOrders,
  );

  const [showGenerate, setShowGenerate] = useState(false);
  // Bumped after generating so the lines panel refetches.
  const [refreshToken, setRefreshToken] = useState(0);

  const isNew = !orderId || orderId === "new";

  /**
   * Arriving from "Copy order" on the list.
   *
   * The prefill travels in router state rather than being fetched here: it is
   * read once, by the list, and nothing about this route can ask for it again —
   * which is the point. A reload lands on an empty create form, not on a second
   * copy of an order the user may have already saved.
   */
  const routeState = location.state as {
    copyData?: ContainerOrderCopyData;
    sourceOrder?: ContainerOrderCopySource;
  } | null;
  const copyData = isNew ? routeState?.copyData : undefined;
  const copySource = isNew ? routeState?.sourceOrder : undefined;

  useEffect(() => {
    if (isNew) {
      dispatch(clearCurrentContainerOrder());
      return;
    }

    dispatch(fetchContainerOrder(orderId));
  }, [dispatch, orderId, isNew]);

  // Leaving the page must not leave a stale order visible on the next open.
  useEffect(() => () => void dispatch(clearCurrentContainerOrder()), [dispatch]);

  const handleGenerate = async (payload: GenerateLinesPayload) => {
    if (!orderId || isNew) return;

    try {
      const result = await dispatch(
        generateContainerLines({ id: orderId, payload }),
      ).unwrap();
      toast.success(t("toasts.generated", { count: result.lines.length }));
      setShowGenerate(false);
      setRefreshToken((n) => n + 1);
      // The generate response carries the order but not its recomputed progress
      // tallies, and the panel's progress bar is built from those. Re-read
      // rather than show a batch that has just changed size.
      dispatch(fetchContainerOrder(orderId));
    } catch (error: any) {
      toast.error(
        typeof error === "string" ? error : t("toasts.generateFailed"),
      );
    }
  };

  if (loading && !current && !isNew) {
    return (
      <div className="flex h-64 items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
      </div>
    );
  }

  return (
    <div>
      {/* Keyed on the order id so saving a *new* order remounts the form.
          Both routes render this same page, so React keeps the form mounted
          across the /new → /:orderId navigation — without a key it would carry
          its edit-mode state (and the files just uploaded) into the saved
          order, leaving a freshly created order sitting in live inputs. */}
      <ContainerOrderForm
        key={isNew ? "new" : orderId}
        order={isNew ? undefined : (current ?? undefined)}
        // A copy is a new order that starts filled in — the form creates it
        // through the same save any other new order goes through.
        copyData={copyData}
        copySource={copySource}
        isEditMode={!isNew}
        loading={loading}
        // Editing the order's invoicing services rewrites the charges on the
        // containers below, so the panel is showing figures the save has just
        // replaced. Only fires when the save actually reached them.
        onSaved={() => setRefreshToken((n) => n + 1)}
      />

      {/* Containers — the same panel the orders list expands to, so every row
          action works here too. The order's schedule is not repeated here: it
          sits in the form's opening row, above. */}
      {!isNew && current && (
        <div className="mx-auto max-w-360 w-full space-y-3 px-3 pb-10 sm:px-4">
          {/* One progress bar per order, and it lives in the panel — which
              renders its own above the table, here and in the orders list
              alike. A second copy in a card of its own said exactly the same
              thing twice, a few pixels apart. */}
          <SectionCard
            icon={Container}
            iconClassName="text-[#0B0F0E]"
            title={t("linesBlock.title")}
            titleClassName="text-[#0B0F0E]"
          >
            <OrderLinesPanel
              orderId={current._id}
              // For the row-level CMR download: the order's direction decides
              // which leg of a container's run the note describes.
              order={current}
              refreshToken={refreshToken}
              onGenerate={() => setShowGenerate(true)}
              canGenerate={current.status !== "cancelled"}
              onBatchChanged={() => dispatch(fetchContainerOrder(current._id))}
            />
          </SectionCard>
        </div>
      )}

      {showGenerate && current && (
        <GenerateLinesModal
          isOpen
          onClose={() => setShowGenerate(false)}
          onGenerate={handleGenerate}
          generating={generating}
          orderNumber={current.orderNumber}
          existingCount={current.lineCount ?? 0}
        />
      )}
    </div>
  );
}
